import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  TextField,
  Button,
  IconButton,
  Modal,
  Box,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  MenuItem,
  Select,
  ThemeProvider,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { PLACES_LOADER_OPTIONS } from "../../utils/googleMaps";
import QuoteSection from "../../components/SBCF/SpecSheet/QuoteSection";
import { SchemaFields } from "../../components/SBCF/SpecSheet/FieldRenderer";
import {
  DEFAULT_SPEC_SHEET_CONFIG,
  sectionsForArea,
  defaultValuesForArea,
} from "../../components/SBCF/SpecSheet/specSheetConfig";
import {
  fetchSpecSheetFormConfig,
  submitSpecSheet,
  uploadSalesAttachments,
  fetchTemplates,
  fetchTemplate,
  deleteTemplate,
} from "../../components/SBCF/SpecSheet/api";
import { selectUser } from "../../features/userSlice";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import {
  PageHeader,
  PageShell,
  SectionHeader,
} from "../../components/UI/Page";
import { LoadingState } from "../../components/UI/States";
import { Notice, useSbcfEmbedded } from "../../components/SBCF/ui";

const MAP_DEFAULT_CENTER = { lat: 41.7, lng: -86.5 };
const MAX_FILE_SIZE_MB = 50;
const MAX_FILES = 6;

// Numbered form section with a header, description, and card body
const Section = ({ step, title, description, children }) => (
  <section className="app-card rounded-2xl border">
    <div className="app-divider flex items-start gap-4 border-b px-5 py-4 sm:px-6">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sm font-bold tabular-nums text-sky-700 dark:text-sky-300">
        {step}
      </span>
      <div className="min-w-0">
        <h2 className="text-lg font-semibold">{title}</h2>
        {description && <p className="app-muted text-sm">{description}</p>}
      </div>
    </div>
    <div className="p-5 sm:p-6">{children}</div>
  </section>
);

// Standalone at /sbcf/rfq it draws its own page shell + header; inside the
// /sbcf dashboard (which already has both) it renders as a section.
const SpecSheetFrame = ({ children }) => {
  const embedded = useSbcfEmbedded();
  if (embedded) return <div className="space-y-6">{children}</div>;
  return <PageShell size="xl">{children}</PageShell>;
};

const BlueprintSpecSheet = () => {
  const { isLoaded: mapsLoaded } = useJsApiLoader(PLACES_LOADER_OPTIONS);

  const user = useSelector(selectUser);
  const embedded = useSbcfEmbedded();
  // The whole form (here and in SchemaFields/QuoteSection) is MUI inputs, and
  // MUI's palette is invisible to Tailwind's `dark:` classes — without this the
  // field labels and typed values stay near-black on the dark section cards.
  const muiTheme = useAppMuiTheme();
  const [config, setConfig] = useState(DEFAULT_SPEC_SHEET_CONFIG);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [salesFiles, setSalesFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [uploadWarning, setUploadWarning] = useState(null);
  const fileInputRef = React.useRef(null);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateMsg, setTemplateMsg] = useState(null);
  const [applySeed, setApplySeed] = useState(null);

  const [mapOpen, setMapOpen] = useState(false);
  const [pendingPin, setPendingPin] = useState(null);
  const [latLng, setLatLng] = useState(null);
  const [addressRequired, setAddressRequired] = useState(false);

  // All generic main-form fields, keyed by field id (seeded with config defaults)
  const [mainValues, setMainValues] = useState(() =>
    defaultValuesForArea(DEFAULT_SPEC_SHEET_CONFIG, "main"),
  );

  const [asap, setAsap] = useState(false);

  const [formData, setFormData] = useState({});
  const [quoteProgress, setQuoteProgress] = useState([]);

  const [floorLevels, setFloorLevels] = useState([
    {
      materials: [
        {
          floorMaterial: "",
          customFloorMaterial: "",
          memberDepth: "",
          customDepth: "",
          spacing: "",
          customSpacing: "",
          deflection: "",
          customDeflection: "",
        },
      ],
    },
  ]);

  const [addFloorInfo, setAddFloorInfo] = useState({});
  const [wallInfo, setWallInfo] = useState({});
  const [roofInfo, setRoofInfo] = useState(() =>
    defaultValuesForArea(DEFAULT_SPEC_SHEET_CONFIG, "roofs"),
  );

  // Load the admin-managed form config; fall back to the built-in default
  useEffect(() => {
    fetchSpecSheetFormConfig()
      .then((saved) => {
        if (saved && Array.isArray(saved.sections)) {
          setConfig(saved);
          setMainValues((prev) => ({
            ...defaultValuesForArea(saved, "main"),
            ...prev,
          }));
          setRoofInfo((prev) => ({
            ...defaultValuesForArea(saved, "roofs"),
            ...prev,
          }));
        }
      })
      .catch(() => {
        /* keep built-in default */
      });
  }, []);

  // The salesperson's saved templates
  useEffect(() => {
    if (!user?.id) return;
    fetchTemplates(user.id)
      .then(setTemplates)
      .catch(() => {});
  }, [user?.id]);

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId) return;
    setTemplateMsg(null);
    try {
      const tpl = await fetchTemplate(selectedTemplateId);
      const v = { ...(tpl.values || {}) };
      const tplLatLng = v.latLng || null;
      const tplAsap = !!v.asap;
      delete v.latLng;
      delete v.asap;
      // Dates are job-specific — don't carry them over
      v.requiredDate = "";

      setMainValues({ ...defaultValuesForArea(config, "main"), ...v });
      setLatLng(tplLatLng);
      setAsap(tplAsap);

      const fd = tpl.form_data || {};
      setRoofInfo({
        ...defaultValuesForArea(config, "roofs"),
        ...(fd.roofInfo || {}),
      });
      setWallInfo(fd.wallInfo || {});
      setAddFloorInfo(fd.addFloorInfo || {});
      setFloorLevels(
        fd.floorLevels?.length
          ? fd.floorLevels
          : [
              {
                materials: [
                  {
                    floorMaterial: "",
                    customFloorMaterial: "",
                    memberDepth: "",
                    customDepth: "",
                    spacing: "",
                    customSpacing: "",
                    deflection: "",
                    customDeflection: "",
                  },
                ],
              },
            ],
      );
      setApplySeed({ products: tpl.products || [], seq: Date.now() });
      setTemplateMsg({ type: "success", text: `Applied "${tpl.name}".` });
    } catch (err) {
      setTemplateMsg({ type: "error", text: err.message });
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplateId) return;
    const tpl = templates.find((t) => t._id === selectedTemplateId);
    if (
      !window.confirm(`Delete the template "${tpl?.name || "this template"}"?`)
    )
      return;
    try {
      await deleteTemplate(selectedTemplateId, user?.id);
      setTemplates((prev) => prev.filter((t) => t._id !== selectedTemplateId));
      setSelectedTemplateId("");
      setTemplateMsg(null);
    } catch (err) {
      setTemplateMsg({ type: "error", text: err.message });
    }
  };

  const address = mainValues.address || "";
  const hasAddress = address.trim().length > 0;
  const hasLocation = hasAddress || !!latLng;

  const completedQuotes = quoteProgress.filter((s) => s.completed);
  const incompleteQuotes = quoteProgress.filter((s) => !s.completed);
  const quotesReady = quoteProgress.length > 0 && incompleteQuotes.length === 0;

  const handleMainChange = (fieldId, value) => {
    setMainValues((prev) => ({ ...prev, [fieldId]: value }));
    if (fieldId === "address" && String(value).trim())
      setAddressRequired(false);
  };

  const openMap = () => {
    setPendingPin(latLng);
    setMapOpen(true);
  };

  const closeMap = () => {
    setMapOpen(false);
    setPendingPin(null);
  };

  // Center the map on the typed address when the modal opens
  const handleMapLoad = (map) => {
    if (latLng) {
      map.panTo(latLng);
      map.setZoom(15);
    } else if (hasAddress && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          const pin = { lat: loc.lat(), lng: loc.lng() };
          setPendingPin(pin);
          map.panTo(pin);
          map.setZoom(15);
        }
      });
    }
  };

  const handleMapClick = (e) => {
    setPendingPin({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  };

  const confirmPin = () => {
    setLatLng(pendingPin);
    setAddressRequired(false);
    setMapOpen(false);
  };

  const handleAsapChange = (e) => {
    setAsap(e.target.checked);
    if (e.target.checked) handleMainChange("requiredDate", "");
  };

  const handleFilesSelected = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    setFileError(null);

    const oversized = picked.find(
      (f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024,
    );
    if (oversized) {
      setFileError(
        `"${oversized.name}" is too large — the limit is ${MAX_FILE_SIZE_MB}MB per file.`,
      );
      return;
    }
    const combined = [...salesFiles, ...picked];
    if (combined.length > MAX_FILES) {
      setFileError(`You can attach up to ${MAX_FILES} files.`);
      return;
    }
    setSalesFiles(combined);
  };

  const removeSalesFile = (index) => {
    setSalesFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!quotesReady || submitting) return;
    // No address typed — make them drop a pin on the map instead
    if (!hasLocation) {
      setAddressRequired(true);
      openMap();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    const submittedBy = {
      id: user?.id,
      name:
        user?.name || user?.fullName || user?.displayName || user?.userID || "",
    };
    try {
      const result = await submitSpecSheet({
        submittedBy: submittedBy,
        products: quoteProgress.map((s) => s.label),
        values: {
          ...mainValues,
          latLng,
          asap,
          requiredDate: asap ? "ASAP" : mainValues.requiredDate || "",
        },
        formData: formData,
        config: config,
      });

      // The form itself is in; a failed file upload shouldn't undo that
      if (salesFiles.length > 0) {
        try {
          await uploadSalesAttachments(result.id, salesFiles, submittedBy);
        } catch (err) {
          setUploadWarning(
            `The form was submitted, but the attachments failed to upload: ${err.message}. Reach out to the SBCF team to send them another way.`,
          );
        }
      }

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    setFormData({
      floorLevels: floorLevels,
      addFloorInfo: addFloorInfo,
      wallInfo: wallInfo,
      roofInfo: roofInfo,
    });
  }, [floorLevels, addFloorInfo, wallInfo, roofInfo]);

  // Custom renderers for the built-in special fields
  const specialRenderers = {
    address: (field) => (
      <div key={field.id} className="sm:col-span-2">
        <TextField
          label={field.label}
          fullWidth
          value={address}
          onChange={(e) => handleMainChange("address", e.target.value)}
          error={addressRequired}
          helperText={
            addressRequired
              ? "Enter an address or pick a location on the map"
              : latLng
                ? `Pinned: ${latLng.lat.toFixed(5)}, ${latLng.lng.toFixed(5)}`
                : "No address? Use the pin icon to pick a spot on the map."
          }
          InputProps={{
            endAdornment: (
              <IconButton onClick={openMap} aria-label="Pick a location on the map">
                <LocationOnIcon color={latLng ? "primary" : "inherit"} />
              </IconButton>
            ),
          }}
        />
      </div>
    ),
    date_asap: (field) => (
      <div key={field.id} className="flex items-center gap-2">
        <TextField
          label={field.label}
          fullWidth
          type="date"
          value={mainValues.requiredDate || ""}
          onChange={(e) => handleMainChange("requiredDate", e.target.value)}
          disabled={asap}
          InputLabelProps={{ shrink: true }}
        />
        <FormControlLabel
          control={<Checkbox checked={asap} onChange={handleAsapChange} />}
          label="ASAP"
          className="whitespace-nowrap"
        />
      </div>
    ),
  };

  const mainSections = sectionsForArea(config, "main");

  if (submitted) {
    return (
      <SpecSheetFrame>
        <div className="mx-auto w-full max-w-2xl py-6 text-center sm:py-10">
          <div className="app-card rounded-2xl border p-8 sm:p-10">
            <span className="app-icon-tile mx-auto mb-4 h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
              <CheckCircleIcon fontSize="large" />
            </span>
            <h1 className="text-2xl font-bold tracking-tight">
              Spec Sheet Submitted
            </h1>
            <p className="app-muted mt-2">
              The SBCF team has your form. Track its status and message the
              team from the <span className="font-medium">My Submissions</span>{" "}
              tab.
            </p>
            {uploadWarning && (
              <Notice tone="amber" className="mt-4 text-left font-medium">
                ⚠ {uploadWarning}
              </Notice>
            )}
            <button
              type="button"
              className="app-btn app-btn-primary app-btn-lg mt-6"
              onClick={() => window.location.reload()}
            >
              Start Another Spec Sheet
            </button>
          </div>
        </div>
      </SpecSheetFrame>
    );
  }

  const titleProps = {
    title: "Blueprint Spec Sheet",
    description: "Work through the sections below.",
  };

  return (
    <ThemeProvider theme={muiTheme}>
    <SpecSheetFrame>
      {embedded ? (
        <SectionHeader {...titleProps} className="!mb-0" />
      ) : (
        <PageHeader
          icon={AssignmentIcon}
          tone="orange"
          eyebrow="SBCF"
          className="!mb-0"
          {...titleProps}
        />
      )}

      {/* Dev warning banner */}
      <Notice tone="amber" className={`font-semibold ${embedded ? "" : "mt-6"}`}>
        ⚠ Still in development — do not use.
      </Notice>

      <div className={embedded ? "space-y-6" : "mt-6 space-y-6"}>
      {/* Start from a saved template */}
      {templates.length > 0 && (
        <div className="app-card flex flex-wrap items-center gap-3 rounded-2xl border p-4">
          <span className="w-full text-sm font-medium">
            Start from a template:
          </span>
          <FormControl size="small" className="w-full sm:w-72">
            <InputLabel>Template</InputLabel>
            <Select
              value={selectedTemplateId}
              label="Template"
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              {templates.map((t) => (
                <MenuItem key={t._id} value={t._id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            disabled={!selectedTemplateId}
            onClick={handleApplyTemplate}
          >
            Apply
          </Button>
          <IconButton
            size="small"
            color="error"
            disabled={!selectedTemplateId}
            onClick={handleDeleteTemplate}
            aria-label="Delete template"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          {templateMsg && (
            <span
              className={`text-sm font-medium ${
                templateMsg.type === "success"
                  ? "text-emerald-700 dark:text-emerald-300"
                  : "text-rose-700 dark:text-rose-300"
              }`}
            >
              {templateMsg.text}
            </span>
          )}
        </div>
      )}

      {mainSections.map((section, index) =>
        section.special === "products" ? (
          <Section
            key={section.id}
            step={index + 1}
            title={section.title}
            description={section.description}
          >
            <QuoteSection
              config={config}
              levels={floorLevels}
              setLevels={setFloorLevels}
              addFloorInfo={addFloorInfo}
              setAddFloorInfo={setAddFloorInfo}
              roofInfo={roofInfo}
              setRoofInfo={setRoofInfo}
              wallInfo={wallInfo}
              setWallInfo={setWallInfo}
              onProgressChange={setQuoteProgress}
              applySelection={applySeed}
            />
          </Section>
        ) : (
          <Section
            key={section.id}
            step={index + 1}
            title={section.title}
            description={section.description}
          >
            <SchemaFields
              fields={section.fields}
              values={mainValues}
              onChange={handleMainChange}
              columns={section.columns || 3}
              specialRenderers={specialRenderers}
            />
          </Section>
        ),
      )}

      {/* Attachments (blueprints, plans, photos) */}
      <Section
        step={mainSections.length + 1}
        title="Attachments"
        description={`Attach blueprints, plans, or photos — up to ${MAX_FILES} files, ${MAX_FILE_SIZE_MB}MB each (optional).`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.docx,.xlsx,.xls,.csv"
          onChange={handleFilesSelected}
        />
        <Button
          variant="outlined"
          startIcon={<AttachFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={salesFiles.length >= MAX_FILES}
        >
          Add Files
        </Button>
        {fileError && (
          <Notice tone="rose" className="mt-3 font-medium">
            {fileError}
          </Notice>
        )}
        {salesFiles.length > 0 && (
          <ul className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
            {salesFiles.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="app-surface flex items-center justify-between gap-2 rounded-xl border px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <InsertDriveFileOutlinedIcon
                    fontSize="small"
                    className="app-muted shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{file.name}</p>
                    <p className="app-muted text-xs tabular-nums">
                      {(file.size / (1024 * 1024)).toFixed(1)} MB
                    </p>
                  </div>
                </div>
                <IconButton
                  size="small"
                  onClick={() => removeSalesFile(index)}
                  aria-label={`Remove ${file.name}`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Submit footer */}
      <div className="app-card flex flex-col items-start justify-between gap-4 rounded-2xl border p-5 sm:flex-row sm:items-center sm:p-6">
        {quoteProgress.length === 0 ? (
          <p className="app-muted text-sm">
            Select at least one product in the products section to submit.
          </p>
        ) : quotesReady ? (
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
            ✓ All {quoteProgress.length} product section
            {quoteProgress.length > 1 ? "s" : ""} complete — ready to submit.
          </p>
        ) : (
          <p className="app-muted text-sm">
            {completedQuotes.length} of {quoteProgress.length} product sections
            complete — still waiting on{" "}
            {incompleteQuotes.map((s) => s.label).join(", ")}.
          </p>
        )}
        <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:items-end">
          {submitError && (
            <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
              {submitError}
            </p>
          )}
          <button
            type="button"
            className="app-btn app-btn-primary app-btn-lg"
            onClick={handleSubmit}
            disabled={!quotesReady || submitting}
          >
            {submitting ? "Submitting..." : "Submit Spec Sheet"}
          </button>
        </div>
      </div>

      </div>

      {/* Location picker modal */}
      <Modal open={mapOpen} onClose={closeMap}>
        <Box className="app-glass app-glass-rim absolute left-1/2 top-1/2 flex h-[85vh] w-[94vw] max-w-4xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-3xl border outline-none">
          <div className="app-divider flex items-center justify-between gap-3 border-b px-5 py-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold">Job Location</h2>
              <p className="app-muted truncate text-sm">
                {hasAddress
                  ? address
                  : "No address entered — click the map to drop a pin."}
              </p>
            </div>
            <IconButton onClick={closeMap} aria-label="Close map">
              <CloseIcon />
            </IconButton>
          </div>

          <div className="min-h-0 flex-1">
            {mapsLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={pendingPin || latLng || MAP_DEFAULT_CENTER}
                zoom={pendingPin || latLng ? 15 : 8}
                onLoad={handleMapLoad}
                onClick={handleMapClick}
              >
                {pendingPin && <Marker position={pendingPin} />}
              </GoogleMap>
            ) : (
              <LoadingState label="Loading map..." className="h-full" />
            )}
          </div>

          <div className="app-divider flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
            <p className="app-muted text-sm tabular-nums">
              {pendingPin
                ? `Pin: ${pendingPin.lat.toFixed(5)}, ${pendingPin.lng.toFixed(
                    5,
                  )}`
                : "Click the map to place a pin."}
            </p>
            <div className="flex gap-2">
              <Button onClick={closeMap}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!pendingPin}
                onClick={confirmPin}
              >
                Use This Location
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
    </SpecSheetFrame>
    </ThemeProvider>
  );
};

export default BlueprintSpecSheet;
